import { Injectable } from '@angular/core';
import { isValidEmail } from '@cdw-ae/common-data';
import type { CsvValidationWarning, CsvValidationResult, CsvRow } from '@cdw-ae/common-data';

@Injectable({
  providedIn: 'root',
})
export class CsvValidationService {
  private readonly MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  private readonly REQUIRED_COLUMNS_FULL = ['User Collection Name', 'Description', 'Email'];
  private readonly REQUIRED_COLUMNS_EMAIL_ONLY = ['Email'];

  /**
   * LAYER 1: Frontend CSV Validation
   *
   * Validates a CSV file on the frontend for immediate feedback WITHOUT database access.
   * This is the first validation layer that provides quick format/structure validation.
   *
   * WHAT THIS LAYER VALIDATES:
   * - File size and type (.csv extension)
   * - CSV structure and parsing
   * - Required columns exist
   * - Email format (regex validation)
   * - Required fields are not empty
   * - Duplicate emails WITHIN the CSV file
   *
   * WHAT THIS LAYER DOES NOT VALIDATE (Backend handles these):
   * - Whether user collections already exist in database
   * - Whether descriptions differ from existing database values
   * - Whether users exist in the system
   * - Any other database-state validation
   *
   * After this validation passes, data is sent to backend for Layer 2 validation.
   *
   * @param file The CSV file to validate
   * @param emailOnlyMode If true, only validates email column (used when adding users to existing collection)
   */
  async validateCsvFile(file: File, emailOnlyMode = false): Promise<CsvValidationResult> {
    const errors: string[] = [];
    const warnings: CsvValidationWarning[] = [];

    // Validate file size
    if (file.size > this.MAX_FILE_SIZE) {
      errors.push(
        `File size exceeds maximum allowed size of ${this.MAX_FILE_SIZE / 1024 / 1024}MB.`
      );
      return { valid: false, errors, warnings };
    }

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.csv')) {
      errors.push('File must be a CSV file with .csv extension.');
      return { valid: false, errors, warnings };
    }

    try {
      // Read and parse CSV
      const content = await this.readFileContent(file);
      const rows = this.parseCsv(content);

      if (rows.length === 0) {
        errors.push('CSV file is empty.');
        return { valid: false, errors, warnings };
      }

      // Validate headers based on mode
      const headers = rows[0];
      const missingColumns = this.validateHeaders(headers, emailOnlyMode);
      if (missingColumns.length > 0) {
        errors.push(
          `Missing required columns: ${missingColumns.join(', ')}`
        );
        return { valid: false, errors, warnings };
      }

      // In email-only mode (editing a collection), reject extra columns beyond "Email"
      if (emailOnlyMode) {
        const extraColumns = this.getExtraColumns(headers);
        if (extraColumns.length > 0) {
          errors.push(
            `CSV contains unexpected columns: ${extraColumns.join(', ')}. The file should only contain: Email.`
          );
          return { valid: false, errors, warnings };
        }
      }

      // Parse data rows
      const data: CsvRow[] = [];
      const columnIndexes = this.getColumnIndexes(headers);

      for (let i = 1; i < rows.length; i++) {
        const row = rows[i];
        if (this.isEmptyRow(row)) continue;

        const email = row[columnIndexes.email]?.trim();
        let hasError = false;

        // Validate email format (always required)
        if (!email) {
          errors.push(
            `Row ${i + 1}: Email address is missing. Data with errors will be skipped. You can cancel the import, fix the error, and upload the CSV again.`
          );
          hasError = true;
        } else if (!isValidEmail(email)) {
          errors.push(
            `The email format "${email}" is not valid. Data with errors will be skipped. You can cancel the import, fix the error, and upload the CSV again.`
          );
          hasError = true;
        }

        // Only validate collection name and description if NOT in email-only mode
        let userCollectionName = '';
        let description = '';

        if (!emailOnlyMode) {
          userCollectionName = row[columnIndexes.userCollection]?.trim();
          description = row[columnIndexes.description]?.trim();

          // Validate user collection name
          if (!userCollectionName) {
            errors.push(
              `Row ${i + 1}: User Collection Name is missing. Data with errors will be skipped. You can cancel the import, fix the error, and upload the CSV again.`
            );
            hasError = true;
          }

          // Per-row blank description is allowed (blank = "no opinion" per FR4)
          // Conflicting descriptions within a collection are checked after all rows are parsed
        }

        // Only add valid rows to the data array
        if (!hasError) {
          data.push({
            rowNumber: i + 1,
            userCollectionName: userCollectionName || '',
            description: description || '',
            email: email || '',
          });
        }
      }

      // Check for duplicate emails in the CSV
      const emailCounts = new Map<string, number>();
      data.forEach((row) => {
        if (row.email) {
          emailCounts.set(row.email, (emailCounts.get(row.email) || 0) + 1);
        }
      });

      const duplicates = Array.from(emailCounts.entries())
        .filter(([, count]) => count > 1)
        .map(([email]) => email);

      if (duplicates.length > 0) {
        warnings.push({
          type: 'duplicate_emails',
          message: `Duplicate email addresses found in CSV: ${duplicates.join(', ')}`,
        });
      }

      return {
        valid: errors.length === 0,
        errors,
        warnings,
        data: data.length > 0 ? data : undefined,
      };
    } catch (error) {
      errors.push(
        `Failed to parse CSV file: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
      return { valid: false, errors, warnings };
    }
  }

  private readFileContent(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as string);
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  }

  private parseCsv(content: string): string[][] {
    const lines = content.split(/\r?\n/);
    const rows: string[][] = [];

    for (const line of lines) {
      if (!line.trim()) continue;

      // Simple CSV parsing (handles quoted fields)
      const row: string[] = [];
      let current = '';
      let inQuotes = false;

      for (let i = 0; i < line.length; i++) {
        const char = line[i];

        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          row.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      row.push(current.trim());
      rows.push(row);
    }

    return rows;
  }

  private validateHeaders(headers: string[], emailOnlyMode: boolean): string[] {
    const normalizedHeaders = headers.map((h) => h.trim().toLowerCase());
    const missing: string[] = [];
    const requiredColumns = emailOnlyMode
      ? this.REQUIRED_COLUMNS_EMAIL_ONLY
      : this.REQUIRED_COLUMNS_FULL;

    for (const required of requiredColumns) {
      if (!normalizedHeaders.includes(required.toLowerCase())) {
        missing.push(required);
      }
    }

    return missing;
  }

  private getColumnIndexes(headers: string[]): {
    userCollection: number;
    description: number;
    email: number;
  } {
    const normalizedHeaders = headers.map((h) => h.trim().toLowerCase());

    return {
      userCollection: normalizedHeaders.indexOf('user collection name'),
      description: normalizedHeaders.indexOf('description'),
      email: normalizedHeaders.indexOf('email'),
    };
  }

  private getExtraColumns(headers: string[]): string[] {
    return headers
      .map((h) => h.trim())
      .filter((h) => h.toLowerCase() !== 'email');
  }

  private isEmptyRow(row: string[]): boolean {
    return row.every((cell) => !cell.trim());
  }
}
